import React from "react";
import { StyleSheet, View, TouchableOpacity } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ActivityLogModel } from "common247";
import { ThemedText } from "@/components/ThemedText";
import { EntityIcon } from "@/components/ui/EntityIcon";
import { ThemedEntityLink } from "@/components/ThemedEntityLink";
import { Spacing } from "@/constants/ThemeColours";

export interface IActivityDensityProps {
  activity: ActivityLogModel;
  actionConfig: { label: string; bg: string; text: string; icon: keyof typeof Ionicons.glyphMap };
  actorName: string;
  timeAgoStr: string;
  isNavigableEntity: boolean;
  cardBg: string;
  activeBorder: string;
  textMuted: string;
  tintColor: string;
  onNavigate?: () => void;
}

/**
 * Renders the single-line compact list view row for an activity log entry.
 */
export function ActivityCompactRow({
  activity,
  actionConfig,
  actorName,
  timeAgoStr,
  isNavigableEntity,
  cardBg,
  activeBorder,
  textMuted,
  tintColor,
  onNavigate,
}: IActivityDensityProps) {
  return (
    <TouchableOpacity
      activeOpacity={0.8}
      onPress={onNavigate}
      style={[
        styles.compactRow,
        { backgroundColor: cardBg, borderColor: activeBorder },
      ]}
    >
      <View style={styles.compactLeft}>
        <View style={[styles.compactActionBadge, { backgroundColor: actionConfig.bg }]}>
          <Ionicons name={actionConfig.icon} size={14} color={actionConfig.text} />
        </View>
        {isNavigableEntity ? (
          <ThemedEntityLink
            type={activity.targetType}
            id={activity.targetId}
            displayName={
              activity.targetTitle
                ? `${activity.description} • ${activity.targetTitle}`
                : activity.description
            }
            variant="inline"
            showIcon={true}
            showOpenIcon={false}
            onPress={onNavigate}
            style={styles.compactLinkContainer}
            textStyle={styles.compactDescription}
            numberOfLines={1}
          />
        ) : (
          <>
            <EntityIcon type={activity.targetType} size={16} color={tintColor} style={styles.entityIcon} />
            <ThemedText style={styles.compactDescription} numberOfLines={1}>
              {activity.description}
            </ThemedText>
          </>
        )}
      </View>

      <View style={styles.compactRight}>
        <ThemedText style={[styles.compactActor, { color: textMuted }]} numberOfLines={1}>
          {actorName}
        </ThemedText>
        <ThemedText style={[styles.compactTime, { color: textMuted }]}>
          {timeAgoStr}
        </ThemedText>
        {activity.targetRoute && (
          <Ionicons name="chevron-forward" size={14} color={textMuted} style={{ marginLeft: 4 }} />
        )}
      </View>
    </TouchableOpacity>
  );
}

/**
 * Renders the small grid card view for an activity log entry.
 */
export function ActivitySmallCard({
  activity,
  actionConfig,
  actorName,
  timeAgoStr,
  isNavigableEntity,
  cardBg,
  activeBorder,
  textMuted,
  tintColor,
  onNavigate,
}: IActivityDensityProps) {
  return (
    <TouchableOpacity
      activeOpacity={0.8}
      onPress={onNavigate}
      style={[
        styles.smallCard,
        { backgroundColor: cardBg, borderColor: activeBorder },
      ]}
    >
      <View style={styles.smallHeader}>
        <View style={[styles.badge, { backgroundColor: actionConfig.bg }]}>
          <Ionicons name={actionConfig.icon} size={12} color={actionConfig.text} />
          <ThemedText style={[styles.badgeText, { color: actionConfig.text }]}>
            {actionConfig.label}
          </ThemedText>
        </View>
        <ThemedText style={[styles.smallTime, { color: textMuted }]}>
          {timeAgoStr}
        </ThemedText>
      </View>

      <View style={styles.smallBody}>
        {isNavigableEntity ? (
          <ThemedEntityLink
            type={activity.targetType}
            id={activity.targetId}
            displayName={activity.targetTitle || activity.description}
            variant="inline"
            showIcon={true}
            showOpenIcon={false}
            onPress={onNavigate}
            style={styles.smallLinkContainer}
            textStyle={styles.smallTitle}
            numberOfLines={2}
          />
        ) : (
          <>
            <EntityIcon type={activity.targetType} size={15} color={tintColor} style={{ marginRight: 6 }} />
            <ThemedText style={styles.smallTitle} numberOfLines={2}>
              {activity.targetTitle || activity.description}
            </ThemedText>
          </>
        )}
      </View>

      <ThemedText style={[styles.smallActor, { color: textMuted }]} numberOfLines={1}>
        By {actorName}
      </ThemedText>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  compactRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 10,
    paddingHorizontal: Spacing.md,
    borderWidth: 1,
    marginBottom: 4,
  },
  compactLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    marginRight: Spacing.sm,
  },
  compactActionBadge: {
    width: 22,
    height: 22,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
  },
  entityIcon: {
    marginRight: 8,
  },
  compactDescription: {
    fontSize: 13,
    fontWeight: "500",
    flex: 1,
  },
  compactRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  compactActor: {
    fontSize: 12,
    maxWidth: 100,
  },
  compactTime: {
    fontSize: 11,
  },
  compactLinkContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
  },
  smallCard: {
    borderWidth: 1,
    padding: Spacing.sm,
    marginBottom: Spacing.xs,
  },
  smallHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  smallBody: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 4,
  },
  smallTitle: {
    fontSize: 12,
    fontWeight: "600",
    flex: 1,
  },
  smallActor: {
    fontSize: 10,
  },
  smallTime: {
    fontSize: 10,
  },
  smallLinkContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
  },
  badge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 3,
    gap: 4,
  },
  badgeText: {
    fontSize: 11,
    fontWeight: "600",
  },
});
